import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, Package, Pencil, Timer, Trash2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "@/components/ui/sonner";
import { EmptyState } from "@/components/common/EmptyState";
import { entryTypeClass } from "@/components/orders/EntryDialogs";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { fmtDate, fmtHours, fmtMinutes, fmtNumber } from "@/lib/format";
import { unitShort } from "@/lib/i18n";

const ICONS = { timar: Timer, ressurs: Truck, vare: Package, merknad: MessageSquare };

const describe = (e) => {
  if (e.type === "timar") return { main: e.catalog_item_name || "Timar", value: `${fmtHours(e.hours)} (${fmtMinutes(e.minutes)})`, sub: e.resource_name };
  if (e.type === "ressurs") return { main: e.resource_name || "Ressurs", value: fmtHours(e.hours), sub: e.includes_driver ? "Sats inkl. førar" : null };
  if (e.type === "vare") return { main: e.catalog_item_name || "Vare", value: `${fmtNumber(e.quantity, 3)} ${unitShort(e.unit)}`, sub: null };
  return { main: "Merknad", value: "", sub: null };
};

export const EntryList = ({ order, entries, canEdit, onEdit, emptyTitle = "Ingen registreringar", emptyDescription, testId = "entry-list" }) => {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [toDelete, setToDelete] = useState(null);

  const remove = useMutation({
    mutationFn: async (entry) => (await api.delete(`/entries/${entry.id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entries", order.id] });
      queryClient.invalidateQueries({ queryKey: ["order", order.id] });
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      queryClient.invalidateQueries({ queryKey: ["history", order.id] });
      toast.success("Registreringa er sletta");
      setToDelete(null);
    },
    onError: (err) => toast.error("Kunne ikkje slette", { description: errorMessage(err) }),
  });

  if (!entries.length) {
    return <EmptyState title={emptyTitle} description={emptyDescription} data-testid={`${testId}-empty`} />;
  }

  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card" data-testid={testId}>
      {entries.map((e) => {
        const Icon = ICONS[e.type] || MessageSquare;
        const d = describe(e);
        const own = e.user_id === user?.id || e.created_by === user?.id;
        const editable = canEdit && (isAdmin || own);
        return (
          <div key={e.id} className="flex min-h-[56px] items-start gap-3 p-3 md:p-4" data-testid={`${testId}-item`} data-entry-type={e.type}>
            <span className={entryTypeClass(e.type)}><Icon className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                <p className="truncate text-sm font-medium">{d.main}</p>
                {d.value ? <p className="num text-sm font-semibold" data-testid={`${testId}-item-value`}>{d.value}</p> : null}
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="num">{fmtDate(e.date)}</span> · {e.user_name}
                {e.created_by_name && e.created_by_name !== e.user_name ? ` (registrert av ${e.created_by_name})` : ""}
                {d.sub ? ` · ${d.sub}` : ""}
              </p>
              {e.note ? <p className="mt-1 whitespace-pre-wrap text-sm">{e.note}</p> : null}
            </div>
            {editable ? (
              <div className="flex shrink-0 items-center gap-1">
                <Button variant="ghost" size="icon" className="h-10 w-10" onClick={() => onEdit(e)} aria-label="Endre" data-testid={`${testId}-edit-button`}><Pencil className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-destructive hover:text-destructive" onClick={() => setToDelete(e)} aria-label="Slett" data-testid={`${testId}-delete-button`}><Trash2 className="h-4 w-4" /></Button>
              </div>
            ) : null}
          </div>
        );
      })}
      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-card" data-testid="entry-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette registreringa?</AlertDialogTitle>
            <AlertDialogDescription>Registreringa blir fjerna frå ordra, men handlinga blir logga i historikken.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-11 bg-card md:h-10" data-testid="entry-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="h-11 bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => remove.mutate(toDelete)} data-testid="entry-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
